import { useDailyMissions } from "@/hooks/useDailyMissions";
import { useAuth } from "@/hooks/useAuth";
import { CheckCircle2, Target, Lock } from "lucide-react";
import { motion } from "framer-motion";

const DailyMissionsBanner = () => {
  const { user } = useAuth();
  const { missions, isLoading } = useDailyMissions();

  if (!user || isLoading || missions.length === 0) return null;

  // Stable daily rotation: use local date string as seed
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
  // Simple hash from date string to get a consistent daily number
  const dailySeed = dateStr.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const todayIndex = dailySeed % missions.length;
  const tomorrowIndex = (todayIndex + 1) % missions.length;

  const todayMission = missions[todayIndex];
  const tomorrowMission = missions[tomorrowIndex];

  const hint: Record<string, string> = {
    share_product: "לחץ על כפתור השיתוף בדף מוצר",
    write_review: "כתוב ביקורת על מוצר שרכשת",
  };

  return (
    <div className="mx-auto max-w-5xl px-4 mt-3">
      <div className="bg-gradient-to-l from-accent/40 via-accent/15 to-transparent border border-accent/30 rounded-2xl px-4 py-3 space-y-2">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Target size={16} className="text-primary" />
          <span className="text-sm font-bold text-foreground">המשימה היומית</span>
        </div>

        <div className="flex gap-2">
          {/* Today's mission */}
          <motion.div
            layout
            className={`flex-1 flex items-center gap-2 rounded-xl px-3 py-2 border transition-all ${
              todayMission.completed
                ? "bg-green-500/10 border-green-500/30"
                : "bg-card/60 border-border/40"
            }`}
          >
            <span className="text-base">{todayMission.emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-primary font-bold mb-0.5">היום</p>
              <p className={`text-xs font-semibold truncate ${todayMission.completed ? "text-green-600 line-through" : "text-foreground"}`}>
                {todayMission.label}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {todayMission.completed ? "✓ הושלם" : `+${todayMission.points} נק׳`}
              </p>
              {!todayMission.completed && hint[todayMission.type] && (
                <p className="text-[9px] text-muted-foreground/70 mt-0.5">
                  {hint[todayMission.type]}
                </p>
              )}
            </div>
            {todayMission.completed && (
              <CheckCircle2 size={14} className="text-green-500 shrink-0" />
            )}
          </motion.div>

          {/* Tomorrow's mission (locked) */}
          <div className="flex-1 flex items-center gap-2 rounded-xl px-3 py-2 border border-border/20 bg-muted/30 opacity-60">
            <Lock size={16} className="text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-muted-foreground font-bold mb-0.5">מחר</p>
              <p className="text-xs font-semibold truncate text-muted-foreground">
                {tomorrowMission.label}
              </p>
              <p className="text-[10px] text-muted-foreground">+{tomorrowMission.points} נק׳</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyMissionsBanner;
